// FRU-23700: read the question a step component is showing, and answer it through that component's own submit. Shared by the
// original form and Hope, whose step components have the same shapes: buttons ({ text, value }) with submit(button), an
// amount slider, one input with submit(), or several inputs with submit(). Only the leaf component's public fields and its
// submit are used, so an answer here is a tap there.
import { QuoteAnswers, QuoteChoice, QuoteStep, matchQuoteChoice, quoteAmount } from './quote-tool';

/** Answers that are dollar amounts or a percent, so a slider or amount step takes them as numbers. */
const AMOUNT_FIELDS = ['propertyValue', 'firstMortgageBalance', 'cashOut', 'newHomeValue', 'estimatedDownPayment', 'desiredLoanAmount'];

/** The form's name for a control, found by reference (step keys and control names differ, e.g. phone and phoneNumber). */
export function quoteControlName(form: any, control: any): string | null {
  try {
    const controls = form && form.controls ? form.controls : {};
    return Object.keys(controls).find((k) => controls[k] === control) || null;
  } catch (e) { return null; }
}

/** A step's question as plain text: markdown and tags removed, one line. */
export function quoteQuestionText(raw: any, fallback: string): string {
  const s = String(raw === null || raw === undefined ? '' : raw)
    .replace(/<[^>]+>/g, ' ').replace(/[*_`#>]+/g, '').replace(/\s+/g, ' ').trim();
  return s || fallback;
}

/** The heading a step shows, for steps whose question lives only in their template (the original form's sliders). */
function shownHeading(inst: any): string {
  try {
    const el = inst && inst.elementRef && inst.elementRef.nativeElement;
    const h = el && el.querySelector ? el.querySelector('.step-title, h1, h2') : null;
    return h ? (h.textContent || '') : '';
  } catch (e) { return ''; }
}

function buttonsOf(inst: any): any[] | null {
  if (Array.isArray(inst.buttonList) && inst.buttonList.length) { return inst.buttonList; }
  if (Array.isArray(inst.buttons) && inst.buttons.length) { return inst.buttons; }
  return null;
}

function choicesOf(buttons: any[]): QuoteChoice[] {
  return buttons
    .filter((b) => b && b.value !== undefined && b.value !== null && b.value !== '')
    .map((b) => ({ value: String(b.value), label: quoteQuestionText(b.text, String(b.value)) }));
}

/** The original form's credit question asks "over 500?" after Poor, with its own two buttons. */
function poorCreditFollowUp(inst: any): any[] | null {
  return inst.poorCreditScore && Array.isArray(inst.poorCredScoreBtns) && inst.poorCredScoreBtns.length ? inst.poorCredScoreBtns : null;
}

/** The question a leaf step component is showing, or null when it isn't one of the shapes above. */
export function readQuoteLeaf(inst: any, form: any, stepKey: string, last: boolean): QuoteStep | null {
  if (!inst) { return null; }
  const titleRaw = (inst.message && inst.message.text) || inst.title || inst.noBrandTitle || shownHeading(inst);
  const followUp = poorCreditFollowUp(inst);
  if (followUp) {
    return {
      key: stepKey, question: quoteQuestionText(inst.under500Title, 'Is your credit score over 500?'), kind: 'choice',
      fields: ['creditScoreOver500'], last,
      choices: followUp.map((b: any) => ({ value: String(b.text).toLowerCase() === 'yes' ? 'yes' : 'no', label: String(b.text) })),
    };
  }
  const buttons = buttonsOf(inst);
  if (buttons) {
    const field = quoteControlName(form, inst.control) || stepKey;
    return { key: stepKey, question: quoteQuestionText(titleRaw, stepKey), kind: 'choice', fields: [field], choices: choicesOf(buttons), last };
  }
  if (Array.isArray(inst.controls) && inst.controls.length) {
    const fields = inst.controls.map((c: any) => quoteControlName(form, c)).filter((n: string | null) => !!n) as string[];
    if (fields.length === inst.controls.length) {
      return { key: stepKey, question: quoteQuestionText(titleRaw, stepKey), kind: 'fields', fields, last };
    }
    return null;
  }
  if (inst.control) {
    const field = quoteControlName(form, inst.control) || stepKey;
    const amount = AMOUNT_FIELDS.indexOf(field) >= 0 || Array.isArray(inst.sliderValues) || typeof inst.submitTypedAmount === 'function';
    return { key: stepKey, question: quoteQuestionText(titleRaw, stepKey), kind: amount ? 'amount' : 'text', fields: [field], last };
  }
  return null;
}

/** The slider notch nearest an amount, as a person dragging it would land. */
export function nearestSliderIndex(values: any[], amount: number): number {
  let best = -1;
  let bestGap = Infinity;
  (values || []).forEach((v: any, i: number) => {
    const n = Number(v);
    if (!isFinite(n)) { return; }
    const gap = Math.abs(n - amount);
    if (gap < bestGap) { best = i; bestGap = gap; }
  });
  return best;
}

/**
 * Answer a leaf step through its own submit. Returns '' when the step took it, or why it didn't. Never presses a step's
 * button for anything but the answer given.
 */
export function answerQuoteLeaf(inst: any, form: any, step: QuoteStep, answers: QuoteAnswers): string {
  if (!inst || step.last) { return 'this is the last step; it is submitted by the person, not by this tool'; }
  const field = step.fields[0];
  const given = answers[field];
  if (step.kind === 'choice') {
    const choices = step.choices || [];
    const hit = matchQuoteChoice(choices, given);
    if (!hit) { return '"' + given + '" is not one of the choices (' + choices.map((c) => c.value).join(', ') + ')'; }
    const followUp = poorCreditFollowUp(inst);
    const source = followUp || buttonsOf(inst) || [];
    const button = followUp
      ? source.find((b: any) => (String(b.text).toLowerCase() === 'yes' ? 'yes' : 'no') === hit.value)
      : source.find((b: any) => String(b.value) === hit.value);
    if (!button || typeof inst.submit !== 'function') { return 'the step has no button for it'; }
    inst.submit(button);
    return '';
  }
  if (step.kind === 'amount') {
    const n = quoteAmount(given);
    if (n === null) { return '"' + given + '" is not an amount'; }
    if (typeof inst.submitTypedAmount === 'function') { inst.submitTypedAmount(n, String(n)); return ''; }
    if (Array.isArray(inst.sliderValues) && inst.sliderValues.length && typeof inst.submit === 'function') {
      // An agent's dollars are exact: far below the lowest notch is a mistake to refuse, not a notch to round up to.
      const lowest = Math.min(...inst.sliderValues.map(Number).filter((v: number) => isFinite(v) && v > 0));
      // $0 only where the slider has a $0 choice (what is owed); elsewhere it is below the lowest choice like any small amount.
      const hasZero = inst.sliderValues.some((v: any) => Number(v) === 0);
      if (field !== 'estimatedDownPayment' && isFinite(lowest) && (n === 0 ? !hasZero : n < lowest / 2)) {
        return '$' + n.toLocaleString('en-US') + ' is below the form\'s lowest choice, $' + lowest.toLocaleString('en-US');
      }
      const idx = nearestSliderIndex(inst.sliderValues, n);
      if (idx < 0) { return 'the slider has no options'; }
      inst.submit(idx);
      return '';
    }
    if (inst.control && typeof inst.submit === 'function') { inst.control.setValue(n); inst.submit(); return ''; }
    return 'the step can\'t take an amount';
  }
  if (typeof inst.submit !== 'function') { return 'the step has no submit'; }
  for (const f of step.fields) {
    const c = form && form.controls ? form.controls[f] : null;
    if (!c) { return 'the form has no ' + f + ' field'; }
    const v = answers[f];
    c.setValue(typeof v === 'string' ? v.trim() : v);
    if (typeof c.markAsTouched === 'function') { c.markAsTouched(); }
  }
  inst.submit();
  return '';
}

/** A 10-digit U.S. phone number from what an agent gave, or '' when it isn't one. */
export function quotePhoneDigits(raw: string): string {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length === 11 && d.charAt(0) === '1') { d = d.slice(1); }
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(d) ? d : '';
}
